import {skipToken} from '@reduxjs/toolkit/query';
import {Flag, type BaseFlagProps} from 'platform/components';
import {HStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {useGetTagsForEntityQuery} from '@dms/api/core';
import {type GetInterestV2ApiArg, useGetInterestV2Query} from '@dms/api/finance';
import type {BusinessCaseResponseBody} from '@dms/api/sales';
import {EntityResourceIds, type GetTagsForEntityArg} from '@dms/api/shared';
import {getInterestStateFlagProps, getInterestTypeFlagProps} from '@dms/shared';

import type {
  CustomerInsightsRecord,
  CustomerInsightsLeadQuality,
} from '../../../types/CustomerInsightsViewModel';
import {getEntityTagFlagProps} from '../../../utils/getEntityTagFlagProps';
import {CustomerInsightsBusinessCaseFlags} from './CustomerInsightsBusinessCaseFlags';
import {
  CustomerInsightsEnrichmentStatus,
  type CustomerInsightsEnrichmentStatusProps,
} from './CustomerInsightsEnrichmentStatus';
import {CustomerInsightsLeadQualityFlag} from './CustomerInsightsLeadQualityFlag';
import {CustomerInsightsOptionalFlag} from './CustomerInsightsOptionalFlag';

interface CustomerInsightsRecordFlagsProps {
  record: CustomerInsightsRecord;
  canReadTags: boolean;
  businessCase?: BusinessCaseResponseBody;
  businessCaseEnrichment?: Pick<
    CustomerInsightsEnrichmentStatusProps,
    'isLoading' | 'isError' | 'retry'
  >;
}

const getTagQueryArgs = (
  record: CustomerInsightsRecord,
  canReadTags: boolean
): GetTagsForEntityArg | typeof skipToken =>
  match({kind: record.kind, canReadTags})
    .with({kind: 'interest', canReadTags: true}, () => ({
      recordId: record.id,
      resourceId: EntityResourceIds.interest,
    }))
    .with({kind: 'businessCase', canReadTags: true}, () => ({
      recordId: record.id,
      resourceId: EntityResourceIds.businessCase,
    }))
    .otherwise(() => skipToken);

const getInterestQueryArgs = (
  record: CustomerInsightsRecord
): GetInterestV2ApiArg | typeof skipToken =>
  match(record)
    .returnType<GetInterestV2ApiArg | typeof skipToken>()
    .with({kind: 'interest'}, (interest) =>
      isNil(interest.leadQuality) ? {id: interest.id} : skipToken
    )
    .otherwise(() => skipToken);

const getServiceCaseFlag = (state: string | undefined): BaseFlagProps | null =>
  match(state)
    .with(Pattern.string, (label) => ({
      label,
      colorScheme: 'neutral' as const,
      isSubtle: true,
    }))
    .otherwise(() => null);

export function CustomerInsightsRecordFlags(props: CustomerInsightsRecordFlagsProps) {
  const interestQuery = useGetInterestV2Query(getInterestQueryArgs(props.record));
  const {currentData: interest} = interestQuery;
  const detailLeadQuality: CustomerInsightsLeadQuality | undefined = match(interest?.leadQuality)
    .with(Pattern.string, (value) => ({value}))
    .otherwise(() => undefined);
  const entityTagsQuery = useGetTagsForEntityQuery(
    getTagQueryArgs(props.record, props.canReadTags)
  );
  const {currentData: entityTags} = entityTagsQuery;
  const enrichmentRetries = [
    props.businessCaseEnrichment?.isError ? props.businessCaseEnrichment.retry : undefined,
    interestQuery.isError ? interestQuery.refetch : undefined,
    entityTagsQuery.isError ? entityTagsQuery.refetch : undefined,
  ].filter(isNotNil);
  const retry = () => enrichmentRetries.forEach((refetch) => refetch());

  return (
    <CustomerInsightsEnrichmentStatus
      isLoading={
        props.businessCaseEnrichment?.isLoading === true ||
        interestQuery.isLoading ||
        interestQuery.isFetching ||
        entityTagsQuery.isLoading ||
        entityTagsQuery.isFetching
      }
      isError={
        props.businessCaseEnrichment?.isError === true ||
        interestQuery.isError ||
        entityTagsQuery.isError
      }
      retry={retry}
    >
      <HStack spacing={1} align="center" wrap>
        {match(props.record)
          .with({kind: 'interest'}, (record) => (
            <>
              <CustomerInsightsOptionalFlag flag={getInterestStateFlagProps(record.state)} />
              <CustomerInsightsOptionalFlag
                flag={match(getInterestTypeFlagProps(record.type))
                  .with(null, () => null)
                  .otherwise((flag) => ({...flag, isSubtle: false}))}
              />
              <CustomerInsightsLeadQualityFlag
                leadQuality={record.leadQuality ?? detailLeadQuality}
              />
            </>
          ))
          .with({kind: 'businessCase'}, (record) => (
            <CustomerInsightsBusinessCaseFlags record={record} businessCase={props.businessCase} />
          ))
          .with({kind: 'serviceCase'}, (record) => (
            <CustomerInsightsOptionalFlag flag={getServiceCaseFlag(record.state)} />
          ))
          .exhaustive()}
        {/* A failed tag lookup intentionally omits tags but keeps record flags usable. */}
        {entityTags?.map((tag) => (
          <Flag
            {...getEntityTagFlagProps(tag)}
            key={tag.valueId}
            size="small"
            data-testid={`customerInsights-record-${props.record.id}-tag-${tag.valueId}`}
          />
        ))}
      </HStack>
    </CustomerInsightsEnrichmentStatus>
  );
}
